import { computed, inject, Injectable } from '@angular/core';
import { ENV_CONFIG } from '@business/environment';
import { OpportunityStore } from '@business/state';
import { TTerminateAppOptions } from '@business/types';
import { takeOnce } from '@business/utils';
import { tap } from 'rxjs';
import { DialogAdapter } from '../../dialogs';
import { DomainService } from '../../domain-bll';
import { MetadataService } from '../../metadata';

@Injectable({
  providedIn: 'root'
})
export class OpportunityService extends DomainService {
  readonly #metadataService = inject(MetadataService);

  protected readonly envConfig = inject(ENV_CONFIG);
  protected readonly opportunityStore = inject(OpportunityStore);
  protected readonly dialogAdapter = inject(DialogAdapter);

  protected readonly startEndpoint = computed(() => 'process/start');
  protected readonly baseEndpoint = computed(() => `${this.envConfig.apiUrl}/${this.envConfig.rootEndpoint}`);

  readonly hasActiveOpportunity = computed(() => this.opportunityStore.opportunityCreated());

  /**
   * @description
   * Terminates the current opportunity and redirects the user to the appropriate URL based on the options provided.
   * @param 2 boolean config indications to alter the exit behavior:
   */
  terminateOpportunity({ shouldRestart, shouldConfirm }: TTerminateAppOptions = { shouldRestart: false, shouldConfirm: false }): void {
    // if the user needs to confirm that they wish to exit the app - the popup will open:
    if (shouldConfirm) {
      const dialogRef = this.dialogAdapter.openConfirm({
        title: 'בטוח שרצית לצאת?',
        message: 'בלחיצה על ״יציאה״ המידע לא יישמר ויהיה צורך להתחיל מחדש',
        iconName: 'terminate-icon',
        footerLayout: 'rows',
        buttons: {
          confirm: 'יציאה',
          cancel: 'לא, חזרה לתהליך '
        },
        hideCloseButton: false
      });

      dialogRef.onDismiss$
        .pipe(
          takeOnce(),
          tap((shouldTerminate: boolean) => {
            if (shouldTerminate) {
              this.#metadataService.terminateApplication();
            }
          })
        )
        .subscribe();

      return;
    }

    // redirect the user to restart the application (instead of default behavior - exit to marketing page)
    if (shouldRestart) {
      this.#metadataService.restartApplication();
      return;
    }

    // default behavior - the application will simply exit to the defaultExitUrl:
    this.#metadataService.terminateApplication();
  }
}
